/**
 * Tek bir primary HEX renkten tüm tema shade'lerini üreten
 * pure-function yardımcılar. Dış bağımlılık yok.
 */

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * Math.max(0, Math.min(1, color)))
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function lighten(hex, amount) {
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s, Math.min(100, l + (100 - l) * amount));
}

export function darken(hex, amount) {
  const [h, s, l] = hexToHsl(hex);
  return hslToHex(h, s, Math.max(0, l * (1 - amount)));
}

export function setAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function getContrastText(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? "#0f172a" : "#ffffff";
}

/**
 * Tek bir primary hex'ten tam renk paletini üretir.
 * colors.js ile 1:1 uyumlu key'ler döner.
 */
export function generatePalette(primaryHex) {
  const p = primaryHex || "#2563eb";
  const contrastText = getContrastText(p);

  return {
    primary: p,
    primaryDark: darken(p, 0.2),
    primaryLight: lighten(p, 0.3),
    primaryDarker: darken(p, 0.35),
    primaryLighter: lighten(p, 0.6),
    primaryMid: lighten(p, 0.1),

    contrastText,

    backgroundGradient: `linear-gradient(135deg, ${p} 0%, ${lighten(p, 0.3)} 100%)`,
    backgroundSoft: "#f8fafd",
    backgroundLogin: `linear-gradient(to bottom right, ${lighten(p, 0.6)}, ${lighten(p, 0.3)})`,
    backgroundLoginCard: `linear-gradient(to bottom right, ${darken(p, 0.2)}, ${p})`,

    border: "#e0e7ef",
    inputBg: "#f1f5f9",
    inputBorder: lighten(p, 0.6),
    inputFocus: lighten(p, 0.3),
    inputPlaceholder: "#94a3b8",
    inputText: "#1e293b",

    white: "#fff",
    grayText: "#6b7280",
    heading: "#1e293b",

    buttonPrimary: darken(p, 0.2),
    buttonPrimaryHover: p,
    buttonDisabled: lighten(p, 0.45),

    chipPatientBg: lighten(p, 0.7),
    chipPatientText: p,
    chipPharmacistBg: "#fce4ec",
    chipPharmacistText: "#be185d",
    chipPendingBg: "#fffbe6",
    chipPendingText: "#b26a00",
    chipApprovedBg: "#e6ffed",
    chipApprovedText: "#1b5e20",
    chipRejectedBg: "#ffebee",
    chipRejectedText: "#b71c1c",
    chipExpiredBg: "#ececec",
    chipExpiredText: "#616161",

    success: "#22c55e",
    successHover: "#16a34a",
    error: "#ef4444",
    errorHover: "#dc2626",
    warning: "#f59e42",
    info: p,
    grey: "#6b7280",
    lightBlue: "#f1f5f9",

    secondary: "#be185d",

    shadow: setAlpha(p, 0.25),
    shadowStrong: setAlpha(p, 0.35),
    hoverBg: setAlpha(p, 0.06),
    activeBg: setAlpha(p, 0.12),
  };
}
